/**
 * AddressAutocompleteField — the "lead with Places Autocomplete" step of Property/Experience
 * creation (Location & Map Functionality Spec, steps 1-6): search a place, pick a suggestion,
 * auto-fill every address field in one shot. The manual field-by-field entry these forms already
 * had stays untouched below this — this is a shortcut in front of it, not a replacement, and
 * degrades to a plain text hint when VITE_GOOGLE_MAPS_API_KEY isn't configured.
 */
import { useEffect, useRef } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { parseGooglePlace, type ParsedPlaceAddress } from '@/lib/parseGooglePlace';

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;

export interface AddressAutocompleteFieldProps {
  onSelect: (address: ParsedPlaceAddress) => void;
  className?: string;
  placeholder?: string;
}

function AutocompleteInput({ onSelect, className, placeholder }: AddressAutocompleteFieldProps) {
  const placesLib = useMapsLibrary('places');
  const inputRef = useRef<HTMLInputElement>(null);
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);

  useEffect(() => {
    if (!placesLib || !inputRef.current) return;
    acRef.current = new placesLib.Autocomplete(inputRef.current, {
      fields: ['geometry', 'name', 'formatted_address', 'address_components'],
      types: ['geocode', 'establishment'],
    });

    const listener = acRef.current.addListener('place_changed', () => {
      const place = acRef.current!.getPlace();
      const parsed = parseGooglePlace(place);
      if (parsed) onSelect(parsed);
      if (inputRef.current) inputRef.current.value = '';
    });

    return () => google.maps.event.removeListener(listener);
  }, [placesLib, onSelect]);

  return (
    <input
      ref={inputRef}
      type="text"
      placeholder={placeholder ?? 'Start typing an address or place name…'}
      aria-label="Search for the address to auto-fill the fields below"
      autoComplete="off"
      className={className}
    />
  );
}

export function AddressAutocompleteField(props: AddressAutocompleteFieldProps) {
  if (!API_KEY) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-500">
        Address autocomplete unavailable — set <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">VITE_GOOGLE_MAPS_API_KEY</code> to enable it. Fill in the fields below manually.
      </div>
    );
  }

  return (
    <APIProvider apiKey={API_KEY}>
      <AutocompleteInput {...props} />
    </APIProvider>
  );
}

export default AddressAutocompleteField;
